import {
  AhTestosteronePanelConsultationUpsell,
  AhWellnessPanelConsultationUpsell,
  ComprehensiveHealthPanelConsultationUpsell,
  FreeTestosteronePanelConsultationUpsell,
  InjectableTrtAndHcgUpsellConsultationUpsell,
  InjectableTrtConsultationUpsell,
  LevonorgestrelConsultationUpsell,
  MetforminTrtConsultationUpsell,
  OndansetronDissolvableTabletTrtConsultationUpsell,
  OndansetronPillTrtConsultationUpsell,
  OralFinasterideConsultationUpsell,
  OralMinoxidilConsultationUpsell,
  OralTrtAndEnclomipheneUpsellConsultationUpsell,
  OvernightShippingConsultationUpsell,
  PeptideBPC157ConsultationUpsell,
  PeptideCJC1295IpamorelinConsultationUpsell,
  PeptideGHKCuConsultationUpsell,
  PriorityProcessingConsultationUpsell,
  SildenafilConsultationUpsell,
  TadalafilConsultationUpsell,
  TrtAndEnclomipheneConsultationUpsell,
  TrtAndHcgConsultationUpsell,
  TrtGelAndEnclomipheneUpsellConsultationUpsell,
} from '@data/consultation-request-upsells';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Pharmacy } from '@enums/pharmacies';
import { Sites } from '@enums/sites';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { AppEnvironment } from '@models/app-environment';

/**
 * Environment variables for use on the Testosterone HQ site, both in production and local environment.
 */
export const testosteroneHqCommonEnvironment = {
  site: Sites.TestosteroneHq,
  domain: 'TestosteroneHQ.com',
  siteId: '22',
  faviconFile: 'testosterone-hq-favicon.ico',
  phone: '1-866-992-2032',
  email: 'support@testosteronehq.com',
  businessHours: 'Monday - Sunday 8 a.m. - 8 p.m. (Central Standard Time - CST)',
  allowedRoutes: [
    '/null',
    '/order',
    '/ed-treatment',
    '/order-complete.php',
    '/choose-a-lab',
    '/consultation/choose-a-lab',
    '/consultation/alleviate-nausea-and-vomiting',
    '/consultation/upload-consult-attachments',
    '/consultation/payment-info',
    '/consultation/submission-error',
    '/consultation/peptide-therapy',
    '/consultation/sexual-performance',
    '/consultation/metformin',
    '/consultation/biotin',
    '/consultation/hair-loss-treatment',
    '/consultation/expedited-shipping',
    '/consultation/trt/request',
    '/trt/consultation-request-completed',
    '/consultation/trt-renewal/request',
    '/trt-renewal/consultation-request-completed',
    '/consultation/priority-processing',
    '/consultation/schedule-consultation',
  ],
  enablePayLater: false,
  enableBitPay: false,
  enableGiftCard: true,
  enableBusinessAccounts: false,
  applePayLabel: 'Analyte Health',
  order: {
    minAgeAllowed: 18,
    showCancellationPolicy: false,
    navigateToOrderAddressRoute: false,
    shouldShowPhoneFieldOnOrderRoute: true,
    hasPhysicianFee: false,
    physicianFeeInCents: 0,
    forceEmailResults: true,
    isPartnerTestingAllowed: false,
    orderPaymentFormLayout: 'stacked',
    enableOrderUpgrade: false,
    enableDynamicOrderUpgrade: false,
    enableTosOnOrderPage: true,
    stdPanelWithMycoplasmaTestId: 3162,
    abTestsWithMycoplasmaTestId: [3377, 3379, 3376],
    comprehensiveWellnessPanelId: 3381,
    isFemaleCondomUpsellAllowed: false,
    triggerFemaleCondomUpsellConsultation: false,
    skipUpsellsForBusinessAccounts: false,
    orderSummary: {
      allowEditingCart: false,
      editCartButtonText: 'Edit',
      displayCartSummaryTableHeaders: false,
      testsPrefix: 'Medication Online',
      showCouponCode: false,
      displayFindTestsModal: false,
    },
    orderComplete: {
      displaySisterSiteBanner: false,
      testOrderedWording: {
        single: 'Preferred Medication/Treatment',
        plural: 'Preferred Medications/Treatments',
      },
    },
  },
  maxAgeAllowed: 81,
  minimumNameLength: 2,
  disableSchedulingRedirectForTreatmentTypes: [ConsultationTreatmentTypes.Trt, ConsultationTreatmentTypes.TrtRenewal],
  consultation: {
    deliveryPharmacyId: Pharmacy.TAHA,
    lastPeriodQuestionId: '97',
    erectileDysfunctionMedicationQuestionId: '234',
    offersEdTreatmentUpsell: true,
    highBloodPressureQuestionId: '228',
    breastfeedingQuestionIds: ['31', '82'],
  },
  ngFindalab: {
    findalabMapId: '8f1673661122dda0',
    searchEndpoint: '/labs/nearPostalCode',
    filterByStates: null,
    inHomeCollectionAllowed: true,
    inHomeCollectionCostInCents: 7500,
    inHomeCollectionProviderIds: [1, 2],
    showPhone: false,
    filterByTestAndSite: true,
    getNetworksFromTests: false,
    verifyTestsCanBeOrdered: false,
    validateSelectedLabNetwork: false,
    searchTitle: 'Find a Lab',
    searchDescription: 'Please choose a nearby lab for your testing.',
    pharmacySearchDescription:
      'Please choose a nearby pharmacy where our clinician can send your prescription once they have reviewed your medical information.',
    locateMsg: 'Use my current location',
    labButtonText: 'Choose This Lab',
    searchButtonText: 'Find',
    showSearchIcon: false,
    labIconClass: 'fa fa-arrow-right',
    searchMessageNoResults:
      'Oops! Sorry, we could not find any labs near that location. Please try your search again with a different or less specific address.',
    inputGroup: {
      container: '',
      field: null,
      button: '',
    },
    resultsZoom: 12,
    choosePharmacySearchTitle: 'Enter Your Location',
    showDayOfWeekFilter: false,
  },
  titles: {
    order: 'Checkout - Testosterone HQ',
    consultationRequest: 'Questionnaire - TestosteroneHQ',
    headlineCheckoutScheduled: '',
    step1Title: 'Find a Lab',
    selectedCenterLabel: 'Lab',
    orderPageHeader: {
      title: "You're almost <strong>Done!</strong>",
      subtitle: 'We just need a few more details before determining your diagnosis.',
    },
    scheduledConsultationRequest: {
      title: 'Clinician Phone Consultation Needed',
      calendarDetails:
        'To ensure compliance with Telehealth regulations in your state, we must arrange a brief phone consultation with one of our clinicians before prescribing medication. This consultation is provided at no additional cost to you.',
      phoneNumberDetails: 'A Clinician will call you at',
      availabilityDetails:
        "Typically, our clinicians call earlier than the anticipated window, so there's no need to be concerned if your appointment appears to be scheduled further than expected.",
    },
  },
  buttons: {
    checkout: 'Place Order',
    selectedCenterChange: 'Change Lab',
  },
  styles: {
    metaThemeColor: '#faf8f6',
    chooseAPharmacy: {
      labButtonClass: '',
      searchButtonClass: '',
      googleMapsLabMarkerFillColor: '',
      googleMapsRecommendedMarkerFillColor: '',
      googleMapsMarkerHoverFillColor: '',
    },
    consultationRequest: {
      headlineClass: 'column is-flex is-justify-content-center pt-5 pb-0 has-text-weight-semibold',
    },
    healthLabsCoupon: {
      buttonColor: 'secondary-orange',
    },
    orderCompleteInstructions: {
      cardContentClass: '',
    },
    orderBetterLab: {
      orderPlaceNotificationClass: 'is-warning',
      currentTestCenterNotificationClass: 'is-info',
      keepCurrentLabButtonClass: 'is-uppercase',
      keepCurrentLabButtonColor: 'secondary-orange',
    },
    testCenter: {
      labButtonClass: '',
      searchButtonClass: 'is-secondary-orange has-text-white',
      googleMapsLabMarkerFillColor: '#f27a53',
      googleMapsRecommendedMarkerFillColor: '#f27a53',
      googleMapsMarkerHoverFillColor: '#f35126',
    },
  },
  shopperApproved: {
    site: 41874,
    token: '404kR01J',
  },
  consultationRequestUpsells: {
    [UpsellSlugs.Levonorgestrel]: LevonorgestrelConsultationUpsell,
    [UpsellSlugs.AhWellnessPanel]: AhWellnessPanelConsultationUpsell,
    [UpsellSlugs.OvernightShipping]: OvernightShippingConsultationUpsell,
    [UpsellSlugs.OndansetronPill]: OndansetronPillTrtConsultationUpsell,
    [UpsellSlugs.OndansetronDissolvableTablet]: OndansetronDissolvableTabletTrtConsultationUpsell,
    [UpsellSlugs.PeptideGHKCu]: PeptideGHKCuConsultationUpsell,
    [UpsellSlugs.PeptideCJC1295Ipamorelin]: PeptideCJC1295IpamorelinConsultationUpsell,
    [UpsellSlugs.PeptideBPC157]: PeptideBPC157ConsultationUpsell,
    [UpsellSlugs.Metformin]: MetforminTrtConsultationUpsell,
    [UpsellSlugs.ComprehensiveHealthPanel]: ComprehensiveHealthPanelConsultationUpsell,
    [UpsellSlugs.AhTestosteronePanel]: AhTestosteronePanelConsultationUpsell,
    [UpsellSlugs.InjectableTrt]: InjectableTrtConsultationUpsell,
    [UpsellSlugs.TrtAndHcg]: TrtAndHcgConsultationUpsell,
    [UpsellSlugs.TrtAndEnclomiphene]: TrtAndEnclomipheneConsultationUpsell,
    [UpsellSlugs.InjectableTrtAndHcgUpsell]: InjectableTrtAndHcgUpsellConsultationUpsell,
    [UpsellSlugs.OralTrtAndEnclomipheneUpsell]: OralTrtAndEnclomipheneUpsellConsultationUpsell,
    [UpsellSlugs.TrtGelAndEnclomipheneUpsell]: TrtGelAndEnclomipheneUpsellConsultationUpsell,
    [UpsellSlugs.Sildenafil]: SildenafilConsultationUpsell,
    [UpsellSlugs.Tadalafil]: TadalafilConsultationUpsell,
    [UpsellSlugs.OralMinoxidil]: OralMinoxidilConsultationUpsell,
    [UpsellSlugs.OralFinasteride]: OralFinasterideConsultationUpsell,
    [UpsellSlugs.FreeTestosteronePanel]: FreeTestosteronePanelConsultationUpsell,
    [UpsellSlugs.PriorityProcessing]: PriorityProcessingConsultationUpsell,
  },
} satisfies Partial<AppEnvironment>;
